// ============================================================
// Client portals (agency white-label) + scheduled reports — admin page.
// Portals: v2 /portals (owner/admin). Schedules: /scheduled-reports.
// ============================================================

import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Link,
    List,
    ListItemButton,
    ListItemText,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, Delete, Edit, OpenInNew, Storefront } from '@mui/icons-material'

import {
    useDeletePortalMutation,
    useGetProjectConfigQuery,
    useListPortalsQuery,
    useUpdatePortalMutation,
} from '@/apis/v2Api'
import {
    PORTAL_REPORT_LABELS,
    apiErrorKind,
    type Portal,
    type PortalReport,
} from '@/apis/portalTypes'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { EmptyState } from '@/components/shared/EmptyState'
import { useProjectId } from '@/hooks/useReportArgs'
import { contrastText } from '@/modules/portal/portalTheme'
import { ReportHeader, ReportStatus, SectionCard } from '@/modules/reports/common'

import { ApiErrorAlert } from './ApiErrorAlert'
import { PortalDomainCard } from './PortalDomainCard'
import { PortalEditorDialog } from './PortalEditorDialog'
import { PortalUsersCard } from './PortalUsersCard'
import { ScheduledReportsSection } from './ScheduledReportsSection'

function PortalSummary({
    portal,
    onEdit,
    onDelete,
}: {
    portal: Portal
    onEdit: () => void
    onDelete: () => void
}) {
    const projectId = useProjectId()
    const [update, updateState] = useUpdatePortalMutation()
    const { branding } = portal
    return (
        <Card>
            <Box
                sx={{
                    bgcolor: branding.primaryColor,
                    color: contrastText(branding.primaryColor),
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    minHeight: 56,
                }}
            >
                {branding.logoUrl ? (
                    <Box
                        component="img"
                        src={branding.logoUrl}
                        alt={branding.productName}
                        sx={{ maxHeight: 32, maxWidth: 180, objectFit: 'contain' }}
                    />
                ) : (
                    <Typography fontWeight={700}>{branding.productName}</Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Box
                    sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: branding.accentColor,
                        border: '2px solid #fff',
                    }}
                />
            </Box>
            <CardContent sx={{ display: 'grid', gap: 1.5 }}>
                <ApiErrorAlert error={updateState.error} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ mr: 1 }}>
                        {branding.productName}
                    </Typography>
                    <Chip
                        size="small"
                        label={portal.status}
                        color={portal.status === 'active' ? 'success' : 'default'}
                        variant="outlined"
                    />
                    {portal.powered_by && (
                        <Tooltip title='The "Powered by YOM" footer is shown.'>
                            <Chip size="small" label="Powered by shown" variant="outlined" />
                        </Tooltip>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Tooltip
                        title={portal.status === 'active' ? 'Disable sign-in' : 'Enable sign-in'}
                    >
                        <Switch
                            checked={portal.status === 'active'}
                            disabled={updateState.isLoading}
                            onChange={e => {
                                void update({
                                    projectId,
                                    id: portal.id,
                                    status: e.target.checked ? 'active' : 'disabled',
                                })
                            }}
                        />
                    </Tooltip>
                    <Button startIcon={<Edit />} onClick={onEdit}>
                        Edit
                    </Button>
                    <Button color="error" startIcon={<Delete />} onClick={onDelete}>
                        Delete
                    </Button>
                </Box>
                {portal.url ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            Portal link:
                        </Typography>
                        <Link
                            href={portal.url}
                            target="_blank"
                            rel="noopener"
                            sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}
                        >
                            {portal.url}
                        </Link>
                        <OpenInNew fontSize="inherit" color="action" />
                        <CopyButton text={portal.url} />
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Portal link: /portal/{portal.slug}
                    </Typography>
                )}
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Visible reports
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {portal.reports.map(r => (
                            <Chip
                                key={r}
                                size="small"
                                label={PORTAL_REPORT_LABELS[r as PortalReport] ?? r}
                            />
                        ))}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )
}

export function PortalsPage() {
    const projectId = useProjectId()
    const portals = useListPortalsQuery(projectId)
    const { data: cfg } = useGetProjectConfigQuery(projectId)
    const ent = cfg?.entitlements
    const [deletePortal, deleteState] = useDeletePortalMutation()
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [editor, setEditor] = useState<{ portal: Portal | null } | null>(null)
    const [toDelete, setToDelete] = useState<Portal | null>(null)

    const list = portals.data ?? []
    const selected = list.find(p => p.id === selectedId) ?? list[0] ?? null
    const forbidden = apiErrorKind(portals.error) === 'forbidden'
    const canRemoveBranding = ent?.removeBranding === true
    const domainAllowed = ent?.portalCustomDomain !== false

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Client portals"
                subtitle="White-label report portals for your clients: your branding, their sub-tenant's data only, plus scheduled email reports."
                actions={
                    !forbidden && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setEditor({ portal: null })}
                        >
                            New portal
                        </Button>
                    )
                }
            />
            <ApiErrorAlert error={deleteState.error} sx={{ mb: 2 }} />

            {portals.error ? (
                <ApiErrorAlert error={portals.error} sx={{ mb: 3 }} />
            ) : (
                <ReportStatus isLoading={portals.isLoading}>
                    {list.length === 0 ? (
                        <Card sx={{ mb: 3 }}>
                            <EmptyState
                                icon={<Storefront />}
                                title="No client portals yet"
                                description="Give each client a branded sign-in to their own reports — on your domain, with your logo and colors."
                                actionLabel="Create a portal"
                                onAction={() => setEditor({ portal: null })}
                            />
                        </Card>
                    ) : (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            {list.length > 1 && (
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <Card>
                                        <List dense disablePadding>
                                            {list.map(p => (
                                                <ListItemButton
                                                    key={p.id}
                                                    selected={p.id === selected?.id}
                                                    onClick={() => setSelectedId(p.id)}
                                                >
                                                    <ListItemText
                                                        primary={p.branding.productName}
                                                        secondary={`/${p.slug}${p.status === 'active' ? '' : ' · disabled'}`}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </Card>
                                </Grid>
                            )}
                            {selected && (
                                <Grid
                                    size={{ xs: 12, md: list.length > 1 ? 9 : 12 }}
                                    sx={{ display: 'grid', gap: 2 }}
                                >
                                    <PortalSummary
                                        portal={selected}
                                        onEdit={() => setEditor({ portal: selected })}
                                        onDelete={() => setToDelete(selected)}
                                    />
                                    <PortalUsersCard portal={selected} />
                                    <PortalDomainCard portal={selected} allowed={domainAllowed} />
                                </Grid>
                            )}
                        </Grid>
                    )}
                </ReportStatus>
            )}

            {forbidden ? (
                <SectionCard title="Scheduled reports">
                    <Alert severity="info">
                        Scheduled reports are managed by organization owners and admins.
                    </Alert>
                </SectionCard>
            ) : (
                <ScheduledReportsSection
                    portals={list}
                    selectedPortalId={selected?.id ?? null}
                    ent={ent}
                />
            )}

            {editor && (
                <PortalEditorDialog
                    portal={editor.portal}
                    canRemoveBranding={canRemoveBranding}
                    onClose={() => setEditor(null)}
                    onSaved={p => {
                        setEditor(null)
                        setSelectedId(p.id)
                    }}
                />
            )}
            <ConfirmDialog
                open={!!toDelete}
                title="Delete portal?"
                message={`"${toDelete?.branding.productName ?? ''}" (/${toDelete?.slug ?? ''}) is deleted with its client logins and the scheduled reports that use it, and its custom domain is released.`}
                confirmLabel="Delete portal"
                confirmColor="error"
                onConfirm={() => {
                    if (toDelete) {
                        void deletePortal({ projectId, id: toDelete.id })
                        if (toDelete.id === selectedId) {
                            setSelectedId(null)
                        }
                    }
                    setToDelete(null)
                }}
                onCancel={() => setToDelete(null)}
            />
        </Box>
    )
}
